import { Link } from "react-router-dom";

export default function SouthAsia() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0FB5AE] to-[#0B253A] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            South Asia → Global Nursing Pathways
          </h1>
          <p className="text-xl md:text-2xl text-white/90">
            Pakistan · India · Nepal · Sri Lanka · Bangladesh
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Intro */}
          <div className="prose prose-lg max-w-none">
            <p className="text-slate-700 leading-relaxed">
              Start here if you're based in South Asia. We support both <strong>aspiring</strong> and <strong>verified</strong> nurses on their global career journey.
            </p>
          </div>

          {/* Popular Routes */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B253A] mb-4">Popular routes</h2>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>USA: NCLEX → CGFNS/VisaScreen → Recruiter match</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>UK/Ireland: NMC → CBT → OSCE → Trust placement</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>GCC: Prometric/HAAD/DHA → Dataflow/TrueProfile → Hospital contract</span>
              </li>
            </ul>
          </div>

          {/* What You Get */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B253A] mb-4">What you get with Nursing Hive</h2>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>Clear checklists and study resources</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>Profile privacy until you opt in for recruiter visibility</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>Matching prioritizes verified profiles</span>
              </li>
            </ul>
          </div>

          {/* By Country Accordion */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B253A] mb-4">By country (requirements & typical flow)</h2>
            <div className="space-y-3">
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-[#0B253A] hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Pakistan
                  <span className="text-[#0FB5AE] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700 space-y-2">
                  <p><strong>NCLEX/English:</strong> Prep first; typical prep takes a few months depending on your schedule.</p>
                  <p><strong>CGFNS/VisaScreen:</strong> Checklist-based documentation and verification.</p>
                  <p><strong>Top destinations:</strong> USA, GCC, UK — choose based on goals and timeline.</p>
                </div>
              </details>
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-[#0B253A] hover:bg-slate-50 transition-colors flex justify-between items-center">
                  India
                  <span className="text-[#0FB5AE] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700 space-y-2">
                  <p><strong>Routes:</strong> USA (NCLEX/CGFNS), UK (CBT/OSCE), GCC (Dataflow).</p>
                  <p><strong>Tip:</strong> Keep transcripts and employment letters handy to speed verification.</p>
                </div>
              </details>
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-[#0B253A] hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Nepal • Sri Lanka • Bangladesh
                  <span className="text-[#0FB5AE] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700 space-y-2">
                  <p><strong>Focus:</strong> Document readiness (Good Standing, transcripts) and English proficiency.</p>
                  <p><strong>Next step:</strong> Build a verified profile and enable <strong>Recruiter Visibility</strong> when ready.</p>
                </div>
              </details>
            </div>
          </div>

          {/* Typical Timeline */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B253A] mb-4">Typical timeline</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-24 font-semibold text-[#0FB5AE]">Month 1–2</div>
                <div className="text-slate-700">Create profile, gather documents, begin NCLEX/English prep</div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-24 font-semibold text-[#0FB5AE]">Month 3–4</div>
                <div className="text-slate-700">Sit NCLEX/CBT; start CGFNS/Dataflow where applicable</div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-24 font-semibold text-[#0FB5AE]">Month 5–6</div>
                <div className="text-slate-700">Verification complete; mark Recruiter Visibility active</div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-24 font-semibold text-[#0FB5AE]">Placement</div>
                <div className="text-slate-700">Interview, offer, onboarding</div>
              </div>
            </div>
          </div>

          {/* Key Notes */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B253A] mb-4">Key notes</h2>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>Verified profiles match faster</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>Document completeness reduces delays</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0FB5AE] mr-2">•</span>
                <span>You can prepare for multiple destinations in parallel</span>
              </li>
            </ul>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="text-2xl font-bold text-[#0B253A] mb-4">Frequently Asked Questions</h2>
            <div className="space-y-3">
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-[#0B253A] hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Do I need experience?
                  <span className="text-[#0FB5AE] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700">
                  No. Students and early-career nurses are welcome; more experience can speed matching.
                </div>
              </details>
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-[#0B253A] hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Can I study while working?
                  <span className="text-[#0FB5AE] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700">
                  Yes. Many candidates prep part-time; our guides include flexible study plans.
                </div>
              </details>
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-[#0B253A] hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Can I apply to multiple destinations?
                  <span className="text-[#0FB5AE] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700">
                  Yes. For example, you can prepare USA NCLEX while completing GCC Dataflow.
                </div>
              </details>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#0FB5AE]/5 rounded-lg p-8 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/signup"
                className="inline-block bg-[#0FB5AE] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#0da59f] transition-colors"
              >
                Register Free
              </Link>
              <Link
                to="/resources"
                className="inline-block border-2 border-[#0FB5AE] text-[#0FB5AE] px-8 py-3 rounded-lg font-semibold hover:bg-[#0FB5AE]/5 transition-colors"
              >
                See Resources
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
